import { useState, useMemo, useRef } from 'react';
import { Spinner, Input, Modal, ModalBody, ModalFooter, Button, Label } from 'reactstrap';
import { Download, CheckCircle, AlertCircle, FileText, RotateCcw, Search } from 'react-feather';
import TablaLocal from '../tabla/TablaLocal';
import ModalLoader from '../../layouts/modals/ModalLoader';
import colors from '../../theme/colors';
import presupuestosApiService from '../../services/http/presupuestosApiService';
import logger from '../../services/logger';

const COLUMNAS = [
  { id: 'id', label: 'ID', width: 120, sortable: true, filter: 'text' },
  { id: 'Nombre', label: 'Nombre', width: 160, sortable: true, filter: 'text' },
  { id: 'Apellidos', label: 'Apellidos', width: 180, sortable: true, filter: 'text' },
  { id: 'Email', label: 'Email', width: 240, sortable: true, filter: 'text' },
  { id: 'Telefono', label: 'Teléfono', width: 130, sortable: true, filter: 'text' },
  { id: 'Ciudad', label: 'Ciudad', width: 140, sortable: true, filter: 'text' },
  { id: 'Empresa', label: 'Empresa', width: 120, sortable: true, filter: 'multi' },
  { id: 'FechaCreacion', label: 'Fecha creación', width: 130, sortable: true, filter: 'text' },
];

const PAGE_LIMIT = 100;
const MAX_PAGES = 200;

const CENTROS = ['CEDESCA', 'CNTEC'];

// Devuelve el centro normalizado (CEDESCA|CNTEC) o null si el contacto no tiene
// uno valido (vacio, "SIN ASIGNAR" o cualquier otra empresa de HubSpot).
const normalizaCentro = (empresa) => {
  const valor = (empresa || '').trim().toUpperCase();
  return CENTROS.includes(valor) ? valor : null;
};

const mapContacto = (c) => ({
  id: c.id,
  Nombre: c.Nombre || '',
  Apellidos: c.Apellidos || '',
  Email: c.Email || '',
  Telefono: c.Telefono || '',
  Ciudad: c.Ciudad || '',
  Empresa: c.Empresa || '',
  FechaCreacion: c.FechaCreacion ? new Date(c.FechaCreacion).toLocaleDateString('es-ES') : '',
});

const ContactosHubspot = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState([]);

  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importError, setImportError] = useState('');
  // Contactos seleccionados sin centro valido: hay que asignarlo a mano antes
  // de importar. asignaciones = { [id]: 'CEDESCA' | 'CNTEC' | '' }.
  const [pendientesCentro, setPendientesCentro] = useState([]);
  const [asignaciones, setAsignaciones] = useState({});

  const cancelLoadRef = useRef(false);

  const seleccion = useMemo(
    () => ({
      estaSeleccionada: (fila) => selected.includes(fila.id),
      alternar: (fila) =>
        setSelected((prev) =>
          prev.includes(fila.id) ? prev.filter((s) => s !== fila.id) : [...prev, fila.id],
        ),
      alternarTodas: (filas, marcar) => {
        const ids = filas.map((f) => f.id);
        setSelected((prev) =>
          marcar ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id)),
        );
      },
    }),
    [selected],
  );

  // Importa la lista ya resuelta (cada contacto con su centro en Empresa).
  const ejecutarImport = async (contactos) => {
    setIsImporting(true);
    setImportError('');
    try {
      const grouped = {};
      contactos.forEach((c) => {
        const hq = normalizaCentro(c.Empresa) || 'CEDESCA';
        if (!grouped[hq]) grouped[hq] = [];
        grouped[hq].push(c);
      });
      const responses = await Promise.all(
        Object.entries(grouped).map(([headquarter, lista]) =>
          presupuestosApiService.importarContactosHubspot({ headquarter, contactos: lista }),
        ),
      );
      const allResults = responses.flat();
      const importedIds = allResults.filter((r) => r.status === 'imported').map((r) => r.id);
      setResults((prev) => prev.filter((r) => !importedIds.includes(r.id)));
      setImportResults(allResults);
      setSelected([]);
    } catch (error) {
      logger.error('Error importando contactos:', error);
      setImportError('Error al importar los contactos.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleImport = () => {
    const contactosToImport = results.filter((r) => selected.includes(r.id));
    // Los que no tienen centro valido (CEDESCA/CNTEC) deben asignarse a mano
    // antes de crear el lead, en vez de fallar en el backend.
    const sinCentro = contactosToImport.filter((c) => !normalizaCentro(c.Empresa));
    if (sinCentro.length > 0) {
      setPendientesCentro(sinCentro);
      setAsignaciones(Object.fromEntries(sinCentro.map((c) => [c.id, ''])));
      return;
    }
    ejecutarImport(contactosToImport);
  };

  const asignarCentro = (id, centro) => setAsignaciones((prev) => ({ ...prev, [id]: centro }));

  const asignarCentroATodos = (centro) =>
    setAsignaciones((prev) => Object.fromEntries(Object.keys(prev).map((id) => [id, centro])));

  const todosAsignados =
    pendientesCentro.length > 0 && pendientesCentro.every((c) => asignaciones[c.id]);

  const cancelarAsignacion = () => {
    setPendientesCentro([]);
    setAsignaciones({});
  };

  const confirmarAsignacion = () => {
    if (!todosAsignados) return;
    const contactosToImport = results
      .filter((r) => selected.includes(r.id))
      .map((c) => (asignaciones[c.id] ? { ...c, Empresa: asignaciones[c.id] } : c));
    setPendientesCentro([]);
    setAsignaciones({});
    ejecutarImport(contactosToImport);
  };

  const cargarTodos = async (startAfter) => {
    cancelLoadRef.current = false;
    setIsLoading(true);
    setLoadError('');
    setSearched(true);

    let after = startAfter;
    let pages = 0;
    let lastError = '';

    try {
      do {
        if (pages >= MAX_PAGES) {
          lastError =
            'Se alcanzó el tope de páginas (20.000 contactos). Verifica el cursor del backend.';
          break;
        }
        if (cancelLoadRef.current) break;

        // eslint-disable-next-line no-await-in-loop
        const data = await presupuestosApiService.listarContactosHubspot({
          limit: PAGE_LIMIT,
          after: after || undefined,
        });

        const batch = Array.isArray(data?.results) ? data.results : [];
        if (batch.length > 0) {
          setResults((prev) => [...prev, ...batch.map(mapContacto)]);
        }

        after = data?.paging?.next?.after || null;
        setNextCursor(after);
        pages += 1;
      } while (after);
    } catch (error) {
      logger.error('Error cargando contactos HubSpot:', error);
      lastError =
        'Error al cargar contactos. Pulsa Reintentar para continuar desde el último punto.';
    } finally {
      if (lastError) setLoadError(lastError);
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setResults([]);
    setSelected([]);
    setNextCursor(null);
    cargarTodos(null);
  };

  const handleRetry = () => {
    cargarTodos(nextCursor);
  };

  const btnStyle = (disabled) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '6px 14px',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    backgroundColor: '#fff',
    color: '#6b7280',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.15s',
  });

  const barraExtra = (
    <div style={{ display: 'flex', gap: '8px' }}>
      <button
        type="button"
        onClick={handleSearch}
        disabled={isLoading}
        style={{
          ...btnStyle(isLoading),
          border: 'none',
          backgroundColor: colors.primary,
          color: '#fff',
        }}
      >
        {isLoading ? <Spinner style={{ height: '13px', width: '13px' }} /> : <Search size={13} />}
        Buscar
      </button>
      {searched && results.length > 0 && (
        <button
          type="button"
          onClick={handleImport}
          disabled={selected.length === 0 || isLoading || isImporting}
          style={{
            ...btnStyle(selected.length === 0 || isLoading || isImporting),
            border: `1px solid ${colors.primary}`,
            color: colors.primary,
          }}
        >
          <Download size={13} />
          Importar ({selected.length})
        </button>
      )}
    </div>
  );

  return (
    <>
      {isImporting && <ModalLoader header="Importando contactos..." />}
      {pendientesCentro.length > 0 && (
        <Modal isOpen centered size="lg">
          <ModalBody>
            <h5 style={{ marginBottom: '8px' }}>Asignar centro</h5>
            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 0 }}>
              {pendientesCentro.length} contacto(s) seleccionado(s) no tienen centro asignado.
              Indica a qué centro debe ir cada lead antes de importarlo.
            </p>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '8px 0 12px',
              }}
            >
              <Label style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>
                Asignar a todos:
              </Label>
              <Input
                type="select"
                bsSize="sm"
                value=""
                style={{ width: 'auto' }}
                onChange={(e) => e.target.value && asignarCentroATodos(e.target.value)}
              >
                <option value="">—</option>
                {CENTROS.map((centro) => (
                  <option key={centro} value={centro}>
                    {centro}
                  </option>
                ))}
              </Input>
            </div>
            <table className="table table-sm" style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th style={{ width: '140px' }}>Centro</th>
                </tr>
              </thead>
              <tbody>
                {pendientesCentro.map((c) => (
                  <tr key={c.id}>
                    <td>{`${c.Nombre} ${c.Apellidos}`.trim() || c.id}</td>
                    <td>{c.Email || '—'}</td>
                    <td>
                      <Input
                        type="select"
                        bsSize="sm"
                        value={asignaciones[c.id] || ''}
                        onChange={(e) => asignarCentro(c.id, e.target.value)}
                        invalid={!asignaciones[c.id]}
                      >
                        <option value="">Selecciona…</option>
                        {CENTROS.map((centro) => (
                          <option key={centro} value={centro}>
                            {centro}
                          </option>
                        ))}
                      </Input>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={cancelarAsignacion}>
              Cancelar
            </Button>
            <Button color="primary" onClick={confirmarAsignacion} disabled={!todosAsignados}>
              Importar ({selected.length})
            </Button>
          </ModalFooter>
        </Modal>
      )}
      {importError && (
        <Modal isOpen centered>
          <ModalBody>
            <p className="text-danger">{importError}</p>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={() => setImportError('')}>
              Cerrar
            </Button>
          </ModalFooter>
        </Modal>
      )}
      {importResults && (
        <Modal isOpen centered size="lg">
          <ModalBody>
            <h5 style={{ marginBottom: '16px' }}>Resultado de la importación de leads</h5>
            <table className="table table-sm" style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Nombre</th>
                  <th>ID Lead</th>
                  <th>Mensaje</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {importResults.map((r) => {
                  const contact = results.find((res) => res.id === r.id);
                  const nombreCompleto = r.nombre || contact?.Nombre || r.id;
                  const isOk = r.status === 'imported';
                  const leadId =
                    r.Id_lead || r.idLead || r.id_lead || r.leadId || r.IdLead || r.ccodcli || r.id;
                  const empresa = (r.empresa || contact?.Empresa || 'CEDESCA').toUpperCase();
                  const handleCrearPresupuesto = () => {
                    if (!leadId) return;
                    localStorage.setItem(
                      'student',
                      JSON.stringify({
                        id: leadId,
                        nombre: contact?.Nombre || nombreCompleto.split(' ')[0] || '',
                        apellidos:
                          contact?.Apellidos || nombreCompleto.split(' ').slice(1).join(' ') || '',
                        empresaId: empresa,
                      }),
                    );
                    window.open('/presupuestos/nuevo?skipSearch=true', '_blank');
                  };
                  return (
                    <tr key={r.id}>
                      <td>
                        {isOk ? (
                          <CheckCircle size={16} color="#059669" />
                        ) : (
                          <AlertCircle size={16} color="#dc2626" />
                        )}
                      </td>
                      <td>{nombreCompleto}</td>
                      <td>{leadId || '—'}</td>
                      <td>
                        {isOk ? (
                          <span style={{ color: '#059669' }}>
                            Lead importado correctamente en {r.empresa || empresa}
                          </span>
                        ) : (
                          <span style={{ color: '#dc2626' }}>
                            {r.reason || 'No se ha podido importar'}
                          </span>
                        )}
                      </td>
                      <td>
                        {isOk && (
                          <Button
                            size="sm"
                            color="primary"
                            onClick={handleCrearPresupuesto}
                            disabled={!leadId}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '0.7rem',
                            }}
                          >
                            <FileText size={12} /> Crear presupuesto
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
              {importResults.filter((r) => r.status === 'imported').length} importado(s),{' '}
              {importResults.filter((r) => r.status !== 'imported').length} con errores o duplicados
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={() => setImportResults(null)}>
              Cerrar
            </Button>
          </ModalFooter>
        </Modal>
      )}
      {(isLoading || loadError) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            marginBottom: '12px',
            borderRadius: '6px',
            backgroundColor: loadError ? '#fef2f2' : '#f0f9ff',
            border: `1px solid ${loadError ? '#fecaca' : '#bae6fd'}`,
            fontSize: '0.75rem',
            color: loadError ? '#b91c1c' : '#075985',
          }}
          data-testid="hubspot-progress"
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            {isLoading && <Spinner style={{ height: '12px', width: '12px' }} />}
            {isLoading
              ? `Cargando contactos… ${results.length} cargados`
              : `${loadError} (${results.length} contactos cargados)`}
          </span>
          {loadError && !isLoading && nextCursor && (
            <button
              type="button"
              onClick={handleRetry}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: `1px solid ${colors.primary}`,
                backgroundColor: '#fff',
                color: colors.primary,
                cursor: 'pointer',
              }}
            >
              <RotateCcw size={12} /> Reintentar
            </button>
          )}
        </div>
      )}
      <TablaLocal
        tablaKey="contactos-hubspot"
        columnas={COLUMNAS}
        filas={results}
        nombreEntidad="contactos"
        archivo="contactos-hubspot"
        placeholder="Buscar por nombre, email, teléfono o ciudad…"
        seleccion={seleccion}
        claveFila={(c) => c.id}
        tituloTarjeta={(c) => `${c.Nombre} ${c.Apellidos}`.trim() || c.id}
        barraExtra={barraExtra}
      />
    </>
  );
};

export default ContactosHubspot;
